import { createUuid } from '@/lib/utils/uuid';

export const ANONYMOUS_ID_STORAGE_KEY = 'ero_anonymous_id';
export const AUTHENTICATED_SESSION_ID_STORAGE_KEY = '__analytics_session_id__';
export const AUTHENTICATED_SESSION_CREATED_STORAGE_KEY = '__analytics_session_created__';
const AUTHENTICATED_SESSION_READY_EVENT = 'authenticated-analytics-session-ready';
const ANONYMOUS_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isValidAnonymousId(value: string): boolean {
  return ANONYMOUS_ID_PATTERN.test(value);
}

export function getStoredAnonymousId(): string | null {
  const storedId = localStorage.getItem(ANONYMOUS_ID_STORAGE_KEY);
  if (storedId === null || isValidAnonymousId(storedId)) return storedId;

  localStorage.removeItem(ANONYMOUS_ID_STORAGE_KEY);
  return null;
}

export function getOrCreateAnonymousId(): string {
  const storedId = getStoredAnonymousId();
  if (storedId !== null) return storedId;

  const anonymousId = createUuid();
  localStorage.setItem(ANONYMOUS_ID_STORAGE_KEY, anonymousId);
  return anonymousId;
}

export function getStoredAuthenticatedSessionId(): string | null {
  return sessionStorage.getItem(AUTHENTICATED_SESSION_ID_STORAGE_KEY);
}

export function getOrCreateAuthenticatedSessionId(): string {
  const storedId = getStoredAuthenticatedSessionId();
  if (storedId !== null) return storedId;

  const sessionId = createUuid();
  sessionStorage.setItem(AUTHENTICATED_SESSION_ID_STORAGE_KEY, sessionId);
  return sessionId;
}

export function markAuthenticatedSessionCreated(): void {
  sessionStorage.setItem(AUTHENTICATED_SESSION_CREATED_STORAGE_KEY, '1');
  window.dispatchEvent(new Event(AUTHENTICATED_SESSION_READY_EVENT));
}

export function isAuthenticatedSessionCreated(): boolean {
  return sessionStorage.getItem(AUTHENTICATED_SESSION_CREATED_STORAGE_KEY) !== null;
}

/** Marks the server-side row as missing without rotating this tab's identity. */
export function invalidateAuthenticatedSessionRegistration(): void {
  sessionStorage.removeItem(AUTHENTICATED_SESSION_CREATED_STORAGE_KEY);
}

/** Ends this tab's identity so the next authenticated account gets a fresh one. */
export function clearAuthenticatedSession(): void {
  sessionStorage.removeItem(AUTHENTICATED_SESSION_CREATED_STORAGE_KEY);
  sessionStorage.removeItem(AUTHENTICATED_SESSION_ID_STORAGE_KEY);
}

/** Runs a consumer only after the server has accepted this tab's session row. */
export function whenAuthenticatedSessionReady(callback: () => void): () => void {
  if (isAuthenticatedSessionCreated()) {
    callback();
    return () => undefined;
  }

  const handleReady = (): void => {
    callback();
  };
  window.addEventListener(AUTHENTICATED_SESSION_READY_EVENT, handleReady, { once: true });
  return () => {
    window.removeEventListener(AUTHENTICATED_SESSION_READY_EVENT, handleReady);
  };
}
